import React from 'react';
import { Lock, ArrowRight, Sparkles, BookOpen } from 'lucide-react';
import { useLocation } from 'wouter';
import { useBilling } from '../BillingContext';

/**
 * Full-page overlay shown when a teacher needs to subscribe.
 * Handles two states:
 *  - 'no_subscription' — brand-new teacher, card-required trial CTA
 *  - 'expired'         — lapsed subscription, resubscribe CTA
 */
export function LockedState({ onViewResources }: { onViewResources?: () => void }) {
  const [, setLocation] = useLocation();
  const { billing } = useBilling();

  const isNewTeacher = billing.status === 'no_subscription';

  return (
    <div className="flex items-center justify-center min-h-[60vh] p-6">
      <div className="max-w-md w-full text-center space-y-6">
        {/* Icon */}
        <div className={`mx-auto w-16 h-16 rounded-2xl flex items-center justify-center
          ${isNewTeacher ? 'bg-primary/10' : 'bg-slate-100'}`}>
          {isNewTeacher
            ? <Sparkles size={28} className="text-primary" />
            : <Lock size={28} className="text-slate-400" />
          }
        </div>

        {/* Message */}
        <div className="space-y-2">
          {isNewTeacher ? (
            <>
              <h2 className="text-xl font-bold text-slate-900">Try TeachBud free for 7 days</h2>
              <p className="text-slate-500 text-sm leading-relaxed">
                Create professional lessons, activity sheets, presentations, and assessments in minutes.
                Start your trial today — your card won't be charged until after the trial ends.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold text-slate-900">Your subscription has ended</h2>
              <p className="text-slate-500 text-sm leading-relaxed">
                Your lessons and resources are safely stored and ready to restore the moment you resubscribe.
              </p>
            </>
          )}
        </div>

        {/* Savings reminder (only for returning teachers) */}
        {!isNewTeacher && billing.resourceStats && billing.resourceStats.total > 0 && (
          <div className="p-4 rounded-2xl bg-primary/5 border border-primary/10 text-left">
            <p className="text-sm font-semibold text-primary mb-2">Your TeachBud history</p>
            <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
              <span>{billing.resourceStats.lessons} lessons created</span>
              <span>{billing.resourceStats.activities} activity sheets</span>
              <span>{billing.resourceStats.presentations} presentations</span>
              <span>{billing.resourceStats.assessments} assessments</span>
            </div>
            {billing.resourceStats.hoursSaved > 0 && (
              <p className="mt-2 text-sm text-primary font-medium">
                ≈ {billing.resourceStats.hoursSaved} hours of planning time saved
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={() => setLocation('/upgrade')}
            className="w-full h-11 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary/90 transition-all flex items-center justify-center gap-2"
          >
            {isNewTeacher ? 'Start your free trial' : 'Resubscribe to TeachBud'}
            <ArrowRight size={16} />
          </button>

          {onViewResources && !isNewTeacher && (
            <button
              onClick={onViewResources}
              className="w-full h-11 rounded-xl border border-slate-200 text-slate-600 font-medium text-sm hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
            >
              <BookOpen size={16} />
              View saved resources (read-only)
            </button>
          )}

          {!isNewTeacher && (
            <button
              onClick={() => setLocation('/account')}
              className="w-full text-center text-sm text-slate-400 hover:text-slate-600 transition-colors py-1"
            >
              View account & billing
            </button>
          )}
        </div>

        {/* Footer note */}
        <p className="text-xs text-slate-400 leading-relaxed">
          {isNewTeacher
            ? 'Your card will be charged $9.99/month or $95.88/year after the trial unless you cancel before it ends.'
            : 'Your data is retained for 12 months. Everything will be restored automatically when you resubscribe.'
          }
        </p>
      </div>
    </div>
  );
}
